// Shared chrome for the dialog family (Import, Add-to-list, …) so every dialog
// gets the same header, close affordance, and footer shell. Layout of what goes
// *inside* the footer stays with each dialog — this only owns the frame.
import { Box, Button, IconButton } from '@mui/material';
import { styled } from '@mui/system';
import {
  DIVIDER, FOOTER_BG, HOVER_BG, SANS_STACK, TEXT_DARK, TEXT_SECONDARY,
} from '../tokens';

// Elevation shared by every dialog Paper.
export const DIALOG_SHADOW = '0 12px 32px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.15)';

// Paper shape shared by every dialog: tight corners, shared shadow, Roboto.
export const DIALOG_PAPER_SX = {
  borderRadius: 4,
  boxShadow: DIALOG_SHADOW,
  overflow: 'hidden',
  fontFamily: SANS_STACK,
  backgroundColor: '#ffffff',
};

export const DialogHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 16px 10px',
  flexShrink: 0,
});

export const DialogTitle = styled('div')({
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: '0.2px',
  color: TEXT_DARK,
});

export const DialogCloseButton = styled(IconButton)({
  width: 24,
  height: 24,
  padding: 0,
  borderRadius: 4,
  color: TEXT_SECONDARY,
  '&:hover': { backgroundColor: HOVER_BG, color: TEXT_DARK },
  '& svg': { fontSize: 16 },
});

// Footer frame only (border, padding, surface). Each dialog sets its own inner
// alignment (gap vs space-between) via sx.
export const DialogFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 12px',
  borderTop: `1px solid ${DIVIDER}`,
  backgroundColor: FOOTER_BG,
  flexShrink: 0,
});

// Low-emphasis footer action (Cancel, secondary links).
export const DialogTextButton = styled(Button)({
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: SANS_STACK,
  color: TEXT_SECONDARY,
  '&:hover': { backgroundColor: HOVER_BG },
});

// Primary footer action. `destructive` swaps the coral for the error red so
// confirm/delete dialogs can reuse the same button.
export const DialogPrimaryButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'destructive',
})(({ theme, destructive }) => {
  const base = destructive ? theme.palette.error.main : theme.palette.primary.main;
  const hover = destructive ? theme.palette.error.dark : theme.palette.primary.dark;
  return {
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: SANS_STACK,
    padding: '4px 16px',
    borderRadius: 4,
    backgroundColor: base,
    color: '#fff',
    '&:hover': { backgroundColor: hover },
    '&.Mui-disabled': { backgroundColor: base, color: '#fff', opacity: 0.5 },
  };
});
